import { GET_CLIENT_DIRECTORY, lawyerDirectoryService } from '@/api';
import { useNotification } from '@/hooks';
import * as React from 'react';
import {
	BaseCaseFilters,
	BulkFlagClient,
	FlagReasonWithCount,
} from '@/interfaces';
import { Button } from '../common';
import { useQueryClient } from '@tanstack/react-query';
import { FlagReasonOption, FlagReasonsPicker } from './FlagReasonsPicker';

interface RemoveFlagBulkModalProps {
	caseId: string;
	userIds: (string | null)[];
	parsedFilters?: BaseCaseFilters;
	hide: () => void;
	onSuccess: () => void;
}

export const RemoveFlagBulkModal: React.FC<RemoveFlagBulkModalProps> = ({
	caseId,
	userIds,
	parsedFilters,
	hide,
	onSuccess,
}) => {
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
	const [isRemovingAll, setIsRemovingAll] = React.useState<boolean>(false);
	const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);
	const [reasonsWithCount, setReasonsWithCount] = React.useState<
		FlagReasonWithCount[]
	>([]);
	const [isLoadingCounts, setIsLoadingCounts] = React.useState(false);
	const { toastSuccess, toastError } = useNotification();
	const queryClient = useQueryClient();

	React.useEffect(() => {
		const fetchCounts = async () => {
			try {
				setIsLoadingCounts(true);
				const response = await lawyerDirectoryService.getFlagReasonsCount({
					...parsedFilters,
					caseId,
					users_ids: userIds.includes(null) ? [] : (userIds as string[]),
				});
				setReasonsWithCount(response.data ?? []);
			} catch (error) {
				toastError('An error has occurred. Please try again.');
			} finally {
				setIsLoadingCounts(false);
			}
		};

		fetchCounts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [caseId, userIds, parsedFilters]);

	const options: FlagReasonOption[] = React.useMemo(
		() =>
			reasonsWithCount.map((reason) => ({
				id: reason.id,
				label: reason.name,
				isCustom: reason.is_custom,
				description: reason.description || undefined,
				count: reason.flagged_clients,
			})),
		[reasonsWithCount]
	);

	const onSubmit = async () => {
		if (isSubmitting || selectedReasons.length === 0) return;
		try {
			setIsSubmitting(true);
			const newData: BulkFlagClient = {
				...parsedFilters,
				caseId,
				users_ids: userIds.includes(null) ? [] : (userIds as string[]),
				reasons: selectedReasons,
				flagged: false,
			};
			await lawyerDirectoryService.postBulkFlagClient(newData);
			toastSuccess('Flag removed from selected clients successfully.');
			hide();
			onSuccess();
			queryClient.invalidateQueries({ queryKey: [GET_CLIENT_DIRECTORY] });
		} catch (error) {
			toastError('The flag could not be removed. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const onRemoveAll = async () => {
		if (isRemovingAll) return;
		try {
			setIsRemovingAll(true);
			const newData: BulkFlagClient = {
				...parsedFilters,
				caseId,
				users_ids: userIds.includes(null) ? [] : (userIds as string[]),
				reasons: [],
				flagged: false,
			};
			await lawyerDirectoryService.postBulkFlagClient(newData);
			toastSuccess('Flag removed from selected clients successfully.');
			hide();
			onSuccess();
			queryClient.invalidateQueries({ queryKey: [GET_CLIENT_DIRECTORY] });
		} catch (error) {
			toastError('The flag could not be removed. Please try again.');
		} finally {
			setIsRemovingAll(false);
		}
	};

	return (
		<div className="flex flex-col md:min-h-[500px]">
			<div className="flex-1">
				<FlagReasonsPicker
					options={options}
					selected={selectedReasons}
					onChange={setSelectedReasons}
					hideSelectedChips
					loading={isLoadingCounts}
					disabled={isSubmitting || isRemovingAll}
				/>
			</div>
			<div className="flex items-center justify-center gap-x-4 mt-8">
				<Button
					label="Remove All"
					decoration="secondary"
					type="button"
					size="full"
					className="!h-12"
					disabled={isSubmitting || isRemovingAll}
					onClick={onRemoveAll}
				/>
				<Button
					label="Confirm"
					decoration="primary"
					type="button"
					size="full"
					className="!h-12"
					onClick={onSubmit}
					disabled={
						isSubmitting || isRemovingAll || selectedReasons.length === 0
					}
				/>
			</div>
		</div>
	);
};
